/**
 * @deprecated This hook causes SSR hydration flicker because it computes responsive
 * values in JavaScript (defaults to "base" on the server, then re-renders after mount).
 *
 * Use Chakra v3 responsive style props instead — they compile to CSS media queries
 * with zero JS and zero flicker:
 *
 *   // ❌ BAD — JS-based, flickers
 *   const size = useBreakpointValue({ base: "full", sm: "6xl" });
 *   <Modal size={size} />
 *
 *   // ✅ GOOD — pure CSS via theme recipe or responsive props
 *   <Modal size="6xl" />  // theme recipe handles smDown automatically
 *   <Box p={{ base: "sm", sm: "lg" }} />
 *
 * For conditional rendering, use CSS display:
 *   <Box display={{ base: "contents", sm: "none" }}><MobileView /></Box>
 *   <Box display={{ base: "none", sm: "contents" }}><DesktopView /></Box>
 */
import { useEffect, useState } from "react";

// Match the breakpoints defined in the theme
const breakpoints = {
  base: "0px",
  xs: "576px",
  sm: "768px",
  md: "1024px",
  lg: "1200px",
  xl: "1440px",
  "2xl": "1600px",
} as const;

function getBreakpoint(): string {
  if (typeof window === "undefined") return "base";
  const width = window.innerWidth;
  if (width >= 1600) return "2xl";
  if (width >= 1440) return "xl";
  if (width >= 1200) return "lg";
  if (width >= 1024) return "md";
  if (width >= 768) return "sm";
  if (width >= 576) return "xs";
  return "base";
}

/** @deprecated Use Chakra v3 responsive style props instead. See top of file for guidance. */
export function useBreakpointValue<T = unknown>(
  values: Partial<Record<string, T>> | T[]
): T | undefined {
  const [currentBreakpoint, setCurrentBreakpoint] =
    useState<string>(getBreakpoint);

  useEffect(() => {
    const updateBreakpoint = () => {
      setCurrentBreakpoint(getBreakpoint());
    };

    // Sync in case window resized between render and effect
    updateBreakpoint();
    window.addEventListener("resize", updateBreakpoint);
    return () => window.removeEventListener("resize", updateBreakpoint);
  }, []);

  if (Array.isArray(values)) {
    const breakpointKeys = Object.keys(breakpoints);
    const currentIndex = breakpointKeys.indexOf(currentBreakpoint);

    // Find the closest value at or before current breakpoint
    for (let i = currentIndex; i >= 0; i--) {
      if (values[i] !== undefined) {
        return values[i];
      }
    }
    return values[0];
  }

  // Handle object format
  const breakpointKeys = Object.keys(breakpoints);
  const currentIndex = breakpointKeys.indexOf(currentBreakpoint);

  // Find the closest value at or before current breakpoint
  for (let i = currentIndex; i >= 0; i--) {
    const key = breakpointKeys[i];
    if (values[key] !== undefined) {
      return values[key];
    }
  }

  return values.base;
}
